import { motion } from 'framer-motion'
import { LCARSPanel, LCARSBar, Panel } from '@enterpress/holomatrix'
import { 
  Bot, 
  Activity, 
  FlaskConical,
  Users,
  Wrench,
  Sparkles
} from 'lucide-react'
import { useTranslation } from '../i18n/index'

/**
 * Crew Section - Pre-built AI Officers
 * 
 * Showcases the three core AI crew members:
 * - First Officer: Operations Commander
 * - Chief Medical Officer: System Health & Recovery
 * - Chief Science Officer: Intelligence & Analytics
 * 
 * Plus Custom Crews and Human Crew integration
 */

export function Crew() {
  const { t } = useTranslation()

  const CREW_OFFICERS = [
    {
      icon: Bot,
      title: t('crew.firstOfficer'),
      role: t('crew.opsCommander'),
      description: t('crew.firstOfficerDesc'),
      capabilities: [t('crew.capabilityConstruction'), t('crew.capabilityCoordination'), t('crew.capabilityOperations')],
      color: 'cyan' as const,
      colorHex: '#00D4FF',
    },
    {
      icon: Activity,
      title: t('crew.medicalOfficer'),
      role: t('crew.healthRecovery'),
      description: t('crew.medicalOfficerDesc'),
      capabilities: [t('crew.capabilityMonitoring'), t('crew.capabilityAutoRecovery'), t('crew.capabilityAdaptiveHealing')],
      color: 'green' as const,
      colorHex: '#00FF88',
    },
    {
      icon: FlaskConical,
      title: t('crew.scienceOfficer'),
      role: t('crew.intelligenceAnalytics'),
      description: t('crew.scienceOfficerDesc'),
      capabilities: [t('crew.capabilityAnalytics'), t('crew.capabilityResearch'), t('crew.capabilityStrategicInsights')],
      color: 'purple' as const,
      colorHex: '#9D8CFF',
    },
  ]

  const ADDITIONAL_CREWS = [
    {
      icon: Wrench,
      title: t('crew.customCrews'),
      description: t('crew.customCrewsDesc'),
      color: '#FFB347',
    },
    {
      icon: Users,
      title: t('crew.humanCrew'),
      description: t('crew.humanCrewDesc'),
      color: '#FF6B6B',
    },
  ]
  return (
    <section id="crew" className="py-24 px-6 relative">
      {/* Background accent */}
      <div className="absolute inset-0 bg-gradient-to-b from-transparent via-[rgba(0,255,136,0.02)] to-transparent pointer-events-none" />

      <div className="max-w-6xl mx-auto relative">
        {/* Section header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <div className="flex items-center justify-center gap-4 mb-6">
            <LCARSBar color="green" size="md" className="w-20" />
            <h2 className="text-sm font-bold tracking-[4px] uppercase text-[#00FF88]">
              {t('crew.title')}
            </h2>
            <LCARSBar color="green" size="md" className="w-20" />
          </div>
          <h3 className="text-4xl md:text-5xl font-bold text-white mb-4">
            {t('crew.subtitle')}
          </h3>
          <p className="text-lg text-[rgba(232,244,248,0.6)] max-w-2xl mx-auto">
            {t('crew.description')}
          </p>
        </motion.div>

        {/* Main crew officers */}
        <div className="grid lg:grid-cols-3 gap-6 mb-12">
          {CREW_OFFICERS.map((officer, index) => (
            <motion.div
              key={officer.title}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.15 }}
            >
              <LCARSPanel 
                title={officer.title} 
                color={officer.color}
                className="h-full"
              >
                <div className="p-6">
                  {/* Role badge */}
                  <div 
                    className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full mb-4"
                    style={{ 
                      backgroundColor: `${officer.colorHex}15`,
                      border: `1px solid ${officer.colorHex}40`,
                    }}
                  >
                    <officer.icon size={14} style={{ color: officer.colorHex }} />
                    <span 
                      className="text-xs font-bold tracking-wider uppercase"
                      style={{ color: officer.colorHex }}
                    >
                      {officer.role}
                    </span>
                  </div>

                  {/* Description */}
                  <p className="text-[rgba(232,244,248,0.7)] leading-relaxed mb-5">
                    {officer.description}
                  </p>

                  {/* Capabilities */}
                  <div className="space-y-2">
                    {officer.capabilities.map((capability) => (
                      <div 
                        key={capability}
                        className="flex items-center gap-2 text-sm"
                      >
                        <div 
                          className="w-1.5 h-1.5 rounded-full"
                          style={{ backgroundColor: officer.colorHex }}
                        />
                        <span className="text-[rgba(232,244,248,0.6)]">{capability}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </LCARSPanel>
            </motion.div>
          ))}
        </div>

        {/* Additional crew options */}
        <div className="grid md:grid-cols-2 gap-6">
          {ADDITIONAL_CREWS.map((crew, index) => (
            <motion.div
              key={crew.title}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.3 + index * 0.1 }}
            >
              <Panel brackets className="p-5 h-full">
                <div className="flex items-start gap-4">
                  <div 
                    className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0"
                    style={{ 
                      backgroundColor: `${crew.color}15`,
                      border: `1px solid ${crew.color}40`,
                    }}
                  >
                    <crew.icon size={22} style={{ color: crew.color }} />
                  </div>
                  <div className="flex-1">
                    <h4 className="text-lg font-bold text-white mb-1">{crew.title}</h4>
                    <p className="text-sm text-[rgba(232,244,248,0.6)]">{crew.description}</p>
                  </div>
                </div>
              </Panel>
            </motion.div>
          ))}
        </div>

        {/* Crew coordination message */}
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          className="mt-12 text-center"
        >
          <div className="inline-flex items-center gap-3 px-5 py-3 rounded-xl bg-[rgba(0,212,255,0.08)] border border-[rgba(0,212,255,0.25)]">
            <Sparkles size={18} className="text-[#00D4FF]" />
            <span className="text-sm text-[rgba(232,244,248,0.7)]">
              {t('crew.coordinationPart1')}{' '}
              <span className="text-[#00D4FF] font-semibold">{t('crew.coordinationHighlight')}</span>{' '}
              {t('crew.coordinationPart2')}
            </span>
          </div>
        </motion.div>
      </div>
    </section>
  )
}

